import { useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import type { ApiError } from "../lib/api";
import { ConsoleShell } from "./shell";
import { Atelier, useSilhouettesInfo } from "./silhouettes";
import { Bascule } from "./silhouettes-reglages";
import { SerreLivres } from "./silhouettes-serre-livres";
import { Chargement, EcranDErreur } from "./states";

/**
 * La page Silhouettes : son titre, le choix entre ses deux modes, et le mode choisi. Elle importe
 * les deux (la page d'une silhouette, `silhouettes.tsx`, et le mode serre-livres,
 * `silhouettes-serre-livres.tsx`) ; aucun des deux ne l'importe.
 */

/**
 * Les deux modes de la page (#337, spec du 4 octobre 2026) : une silhouette, celle d'aujourd'hui,
 * ou un serre-livres, deux figurines et leurs jeux complets. Le choix passe dans l'adresse
 * (`?mode=serre-livres`, `routes.tsx`) : un rechargement ou un lien copie y retombent. Changer de
 * mode remplace la page : ce que l'autre mode montrait, images comprises, est perdu, comme en
 * quittant le module (rien n'est garde).
 */
export type ModeSilhouettes = "silhouette" | "serre-livres";

const MODES: readonly { valeur: ModeSilhouettes; libelle: string }[] = [
  { valeur: "silhouette", libelle: "Une silhouette" },
  { valeur: "serre-livres", libelle: "Un serre-livres" },
];

export function SilhouettesPage({ mode = "silhouette" }: { mode?: ModeSilhouettes }) {
  const infos = useSilhouettesInfo();
  // Une session perdue en cours de route (401, 403 sur un rendu) remplace la page, comme a
  // l'ouverture.
  const [refus, setRefus] = useState<ApiError | null>(null);

  return (
    <ConsoleShell connecte={infos.isSuccess && refus === null}>
      {infos.isPending ? <Chargement quoi="des silhouettes" /> : null}
      {infos.isError ? <EcranDErreur error={infos.error} quoi="les silhouettes" /> : null}
      {refus ? <EcranDErreur error={refus} quoi="les silhouettes" /> : null}
      {infos.data && !refus ? (
        <>
          <EnTete mode={mode} />
          {mode === "serre-livres" ? (
            <SerreLivres infos={infos.data} surRefus={setRefus} />
          ) : (
            <Atelier infos={infos.data} surRefus={setRefus} />
          )}
        </>
      ) : null}
    </ConsoleShell>
  );
}

/** Le titre, sa phrase (celle du mode), et le choix du mode : une bascule, comme les autres reglages. */
function EnTete({ mode }: { mode: ModeSilhouettes }) {
  const navigate = useNavigate();
  const id = useId();
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-5">
      <div>
        <h1 className="text-[21px] font-semibold md:text-2xl">Silhouettes</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {mode === "serre-livres" ? (
            <>
              <span className="md:hidden">
                Une image ou une paire, des pièces à imprimer. Rien n'est gardé ici.
              </span>
              <span className="hidden md:inline">
                Une image ou une paire, des pièces à imprimer pour la base modulaire. Rien n'est
                gardé ici : tes fichiers partent dans tes Téléchargements.
              </span>
            </>
          ) : (
            <>
              <span className="md:hidden">
                Une image, une pièce à imprimer. Rien n'est gardé ici.
              </span>
              <span className="hidden md:inline">
                Une image, une pièce à imprimer pour la base modulaire. Rien n'est gardé ici : tes
                fichiers partent dans tes Téléchargements.
              </span>
            </>
          )}
        </p>
      </div>
      <div>
        <span className="sr-only" id={id}>
          Mode de la page
        </span>
        <Bascule
          changer={(valeur) =>
            void navigate({
              to: "/silhouettes",
              search: valeur === "serre-livres" ? { mode: "serre-livres" } : {},
              replace: true,
            })
          }
          etale
          idLibelle={id}
          options={MODES.map(({ valeur, libelle }) => ({ valeur, libelle }))}
          valeur={mode}
        />
      </div>
    </div>
  );
}
